import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Chip, { chipClasses } from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import useDispatchNumberLabel from '~hooks/useDispatchNumberLabel';

interface OrderSearchMatchBadgeProps {
  searchMatches: string[];
}

// Coarse matched-field keys the client-side derivation reports, mapped to their i18n
// label key. Keys not present in this map are ignored so an unexpected key never renders
// as a raw string.
const MATCH_LABEL_KEY_BY_FIELD: Record<string, string> = {
  contact: 'dispatch.omni_search.match_labels.contact',
  customer: 'dispatch.omni_search.match_labels.customer',
  dispatch_number: 'dispatch.omni_search.match_labels.dispatch_number',
  driver: 'dispatch.omni_search.match_labels.driver',
  equipment: 'dispatch.omni_search.match_labels.equipment',
  order: 'dispatch.omni_search.match_labels.order',
  project: 'dispatch.omni_search.match_labels.project',
  site: 'dispatch.omni_search.match_labels.site',
  ticket: 'dispatch.omni_search.match_labels.ticket',
  vendor: 'dispatch.omni_search.match_labels.vendor',
};

// The badge lives inline in the card header, so it must stay compact. At most this
// many category chips render; any beyond collapse into a single "+N" overflow chip
// whose tooltip lists the remaining categories.
const MAX_VISIBLE_CATEGORY_CHIPS = 2;

export default function OrderSearchMatchBadge({
  searchMatches,
}: OrderSearchMatchBadgeProps) {
  const theme = useTheme();
  const { singular } = useDispatchNumberLabel();

  const knownMatchedFields = searchMatches.filter(
    (field) => field in MATCH_LABEL_KEY_BY_FIELD,
  );

  if (knownMatchedFields.length === 0) {
    return null;
  }

  const visibleFields = knownMatchedFields.slice(0, MAX_VISIBLE_CATEGORY_CHIPS);
  const overflowFields = knownMatchedFields.slice(MAX_VISIBLE_CATEGORY_CHIPS);
  const resolveLabel = (field: string) =>
    field === 'dispatch_number'
      ? singular(MATCH_LABEL_KEY_BY_FIELD[field])
      : t(MATCH_LABEL_KEY_BY_FIELD[field]);
  const overflowLabels = overflowFields.map(resolveLabel);

  // Subtle neutral gray pill, mode-aware via the brandV2 neutral ramp so it reads in
  // both light and dark themes and stays visually distinct from the yellow primary
  // status chips. 20px height + 11px uppercase label keep it inline-compact.
  const chipSx = {
    bgcolor: theme.brandV2.colors.treadGray10,
    border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
    borderRadius: 1,
    height: 20,
    [`& .${chipClasses.label}`]: {
      color: 'text.secondary',
      fontSize: '11px',
      fontWeight: 600,
      px: 0.75,
      textTransform: 'uppercase',
    },
  } as const;

  return (
    <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      <Typography sx={{ color: 'text.secondary', fontSize: '11px', fontWeight: 500 }}>
        {t('dispatch.omni_search.found_in')}
      </Typography>
      {visibleFields.map((field) => (
        <Chip
          color="default"
          data-test-id={`order-search-match-badge__chip-${field}`}
          key={field}
          label={resolveLabel(field)}
          size="small"
          sx={chipSx}
        />
      ))}
      {overflowLabels.length > 0 && (
        <Tooltip title={overflowLabels.join(', ')}>
          <Chip
            color="default"
            data-test-id="order-search-match-badge__overflow"
            label={`+${overflowLabels.length}`}
            size="small"
            sx={chipSx}
          />
        </Tooltip>
      )}
    </Box>
  );
}
